import { useEffect, useState } from "react";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import { es } from "date-fns/locale";
import api from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import { Loader2, MousePointerClick, Users2, ExternalLink, CheckCheck, Ban, Radar } from "lucide-react";

const ASSISTED_TOOLTIP = "Esta red no permite que otras herramientas publiquen automáticamente. La plataforma te deja el post listo y vos lo publicás con un toque.";

const NETWORK_METRIC_LABELS = {
  likes: "Me gusta", comments: "Comentarios", impressions: "Impresiones", reach: "Alcance",
  views: "Vistas", replies: "Respuestas", saves: "Guardados", clicks: "Clics (red)", shares: "Compartidos",
  retweet_count: "Retweets", reply_count: "Respuestas", like_count: "Me gusta", quote_count: "Citas",
  impression_count: "Impresiones", bookmark_count: "Guardados",
};

function NetworkMetricsCard({ metrics, updatedAt }) {
  const status = metrics?.status;
  if (!status) {
    return (
      <div className="border border-dashed border-[#E4E4E7] rounded-md p-3 text-xs text-[#A1A1AA]" data-testid="network-metrics-none">
        Todavía no se consultó a la red.
      </div>
    );
  }
  if (status === "no_disponible") {
    return (
      <div className="border border-[#E4E4E7] rounded-md p-3 bg-[#FAFAFA]" data-testid="network-metrics-not-available">
        <p className="text-xs text-[#71717A]">Esta red no informa esta métrica.</p>
        {metrics.reason && <p className="text-xs text-[#A1A1AA] mt-1">{metrics.reason}</p>}
      </div>
    );
  }
  if (status === "no_implementado") {
    return (
      <div className="border border-[#FDE68A] rounded-md p-3 bg-[#FFFBEB]" data-testid="network-metrics-not-implemented">
        <p className="text-xs text-[#92400E]">Todavía no conectamos las métricas de esta red.</p>
        {metrics.reason && <p className="text-xs text-[#92400E]/80 mt-1">{metrics.reason}</p>}
      </div>
    );
  }
  if (status === "error") {
    return (
      <div className="border border-[#FCA5A5] rounded-md p-3 bg-[#FEF2F2]" data-testid="network-metrics-error">
        <p className="text-xs text-[#991B1B]">No se pudo consultar.</p>
        {metrics.reason && <p className="text-xs text-[#991B1B]/80 mt-1">{metrics.reason}</p>}
      </div>
    );
  }
  const numericEntries = Object.entries(metrics).filter(
    ([k, v]) => !["status", "reason", "fetched_at", "impressions_status", "impressions_reason"].includes(k) && typeof v === "number",
  );
  return (
    <div data-testid="network-metrics-ok">
      <div className="flex flex-wrap gap-2">
        {numericEntries.map(([key, value]) => (
          <span key={key} className="text-xs px-2 py-1 rounded-sm bg-[#F4F4F5] text-[#3F3F46]" data-testid={`network-metric-${key}`}>
            {NETWORK_METRIC_LABELS[key] || key}: {value}
          </span>
        ))}
      </div>
      {metrics.impressions_status && metrics.impressions_status !== "ok" && (
        <p className="text-xs text-[#92400E] mt-2">{metrics.impressions_reason}</p>
      )}
      {updatedAt && (
        <p className="text-xs text-[#A1A1AA] mt-2">
          Actualizado {formatDistanceToNow(new Date(updatedAt), { addSuffix: true, locale: es })}
        </p>
      )}
    </div>
  );
}

export default function PublicationDetailSheet({ publicationId, open, onOpenChange }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    if (!publicationId) return;
    setLoading(true);
    api.get(`/metrics/publications/${publicationId}`).then((r) => setData(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => {
    if (open && publicationId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, publicationId]);

  const markPublished = async () => {
    setBusy(true);
    try {
      await api.post(`/social/publications/${publicationId}/mark-published-manual`);
      toast.success("Marcada como publicada");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo confirmar");
    } finally {
      setBusy(false);
    }
  };

  const discard = async () => {
    setBusy(true);
    try {
      await api.post(`/social/publications/${publicationId}/discard`);
      toast.success("Publicación descartada");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo descartar");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto" data-testid="publication-detail-sheet">
        <SheetHeader>
          <SheetTitle className="font-heading text-left">Detalle de publicación</SheetTitle>
        </SheetHeader>

        {loading || !data ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#0055FF]" /></div>
        ) : (
          <div className="space-y-5 mt-4">
            <p className="text-sm text-[#27272A]">{data.publication.text}</p>
            <div className="flex gap-2 text-xs">
              <Badge variant="outline" className="rounded-sm capitalize">{data.publication.platform}</Badge>
              <Badge variant="outline" className="rounded-sm">{data.publication.status}</Badge>
              {["pending_manual", "expired_unpublished"].includes(data.publication.status) && (
                <Badge className="border-0 rounded-sm bg-[#8ED500]/15 text-[#4D7A00] cursor-help" title={ASSISTED_TOOLTIP} data-testid="publication-detail-assisted-badge">
                  Asistida — la publicás vos
                </Badge>
              )}
            </div>

            {["pending_manual", "expired_unpublished"].includes(data.publication.status) && (
              <div className="flex flex-wrap gap-2 border border-[#E4E4E7] rounded-md p-3 bg-[#FAFAFA]" data-testid="publication-detail-assisted-actions">
                {data.publication.platform === "nextdoor" && (
                  <Button asChild size="sm" variant="outline" className="rounded-md" data-testid="publication-detail-publish-nextdoor">
                    <a href={data.nextdoor_share_url} target="_blank" rel="noreferrer">
                      <ExternalLink className="h-3.5 w-3.5 mr-1" /> Publicar en Nextdoor
                    </a>
                  </Button>
                )}
                {data.publication.platform === "tiktok" && (
                  <p className="text-xs text-[#71717A] w-full" data-testid="publication-detail-tiktok-status">
                    {data.publication.assisted_delivered_at
                      ? "Se envió a tu buzón de notificaciones de TikTok -- abrí la app y terminá de publicarlo."
                      : data.publication.failure_reason
                        ? `No se pudo enviar: ${data.publication.failure_reason}`
                        : "Todavía no se envió al buzón de TikTok (se entrega en su hora programada)."}
                  </p>
                )}
                <Button size="sm" className="bg-[#10B981] hover:bg-[#0F9D6F] rounded-md" disabled={busy} onClick={markPublished} data-testid="publication-detail-mark-published">
                  <CheckCheck className="h-3.5 w-3.5 mr-1" /> Ya la publiqué
                </Button>
                <Button size="sm" variant="ghost" className="text-[#EF4444] rounded-md" disabled={busy} onClick={discard} data-testid="publication-detail-discard">
                  <Ban className="h-3.5 w-3.5 mr-1" /> Descartar
                </Button>
              </div>
            )}

            <div>
              <h4 className="text-xs uppercase tracking-wider text-[#71717A] font-medium mb-2">Medido por nosotros</h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="border border-[#E4E4E7] rounded-md p-3">
                  <div className="flex items-center gap-1.5 text-[#71717A] text-xs"><MousePointerClick className="h-3.5 w-3.5" /> Clics</div>
                  <p className="text-2xl font-heading font-bold text-[#09090B]" data-testid="publication-detail-clicks">{data.click_count}</p>
                </div>
                <div className="border border-[#E4E4E7] rounded-md p-3">
                  <div className="flex items-center gap-1.5 text-[#71717A] text-xs"><Users2 className="h-3.5 w-3.5" /> Leads</div>
                  <p className="text-2xl font-heading font-bold text-[#09090B]" data-testid="publication-detail-leads-count">{data.leads.length}</p>
                </div>
              </div>
            </div>

            <div>
              <h4 className="text-xs uppercase tracking-wider text-[#71717A] font-medium mb-2 flex items-center gap-1.5">
                <Radar className="h-3.5 w-3.5" /> Según la red
              </h4>
              <NetworkMetricsCard metrics={data.network_metrics} updatedAt={data.network_metrics_updated_at} />
            </div>

            {data.clicks_by_day.length > 0 && (
              <div className="h-48" data-testid="publication-detail-clicks-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={data.clicks_by_day}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                    <XAxis dataKey="day" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                    <Tooltip />
                    <Line type="monotone" dataKey="count" stroke="#0055FF" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}

            <Separator />

            <div>
              <h4 className="text-xs uppercase tracking-wider text-[#71717A] font-medium mb-2">Leads atribuidos</h4>
              {data.leads.length === 0 ? (
                <p className="text-xs text-[#A1A1AA]">Todavía no hay leads de esta publicación.</p>
              ) : (
                <div className="space-y-2" data-testid="publication-detail-leads-list">
                  {data.leads.map((l) => (
                    <div key={l.id} className="text-xs border border-[#E4E4E7] rounded-md p-2.5 flex justify-between">
                      <span>{l.name}</span>
                      <Badge variant="outline" className="rounded-sm">{l.status}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
